import type { ReactNode } from 'react';

import { configuration } from '@src/entities/Event/includes/configuration';
import { DefaultListEventsPayload, EventEntity, ListEventsPayload } from '@src/entities/Event/includes/types';
import { SourcesTooltip } from '@src/entities/Source/ui/components/tooltips/Sources';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableQuery';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isUndefined, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import { Filters } from './components/Filters';
import tableConfiguration from './includes/configuration.json';
import { Styles } from './includes/styles';

type TableColumn = {
  [key: string]: unknown;
  accessor: string;
  Cell?: (props: any) => ReactNode;
  Header: string;
  order?: number;
};

type CodeAsset = {
  codeDesc: string;
  codeId: string;
};

type Assets = {
  ListEventTypes?: {
    data?: CodeAsset[];
  };
  ListImpactedSystems?: {
    data?: CodeAsset[];
  };
};

type EventsProps = {
  assets?: Assets;
  error?: unknown;
  isLoading?: boolean;
  requestArgs?: ListEventsPayload;
  requestFunction?: (...args: unknown[]) => void;
  requestKey?: string;
  rows?: EventEntity[];
  setCurrentEntity?: (event: EventEntity) => void;
  setRequestArgs?: (args: ListEventsPayload) => void;
  setShowModal?: (open: boolean) => void;
  totalPages?: number;
  totalRecords?: number;
};

/**
 * Table: Events
 *
 * Displays a list of events within a DataTable.
 */
export function Table({
  assets = {},
  error = {},
  isLoading = false,
  requestArgs = DefaultListEventsPayload,
  requestFunction = () => {},
  requestKey = '',
  rows = [],
  setCurrentEntity = () => {},
  setRequestArgs = () => {},
  setShowModal = () => {},
  totalPages = 1,
  totalRecords = 0,
}: EventsProps) {
  /**
   * Renders the actions cell with a dropdown menu for each row.
   */
  const renderCellActions = ({ row }: { row: { original: EventEntity } }): ReactNode => {
    const menuItems = [
      {
        icon: <IconSymbol icon="edit" />,
        onClick: () => {
          setShowModal(true);
          setCurrentEntity(row.original);
        },
        title: 'Edit',
      },
    ];

    return (
      <div style={{ marginLeft: '-10px', marginRight: '-10px' }}>
        <Layout.Group css={{ height: '100%', marginLeft: '2px', marginRight: '2px' }}>
          <DropdownMenu
            before={<IconSymbol color="#6F6F6F" icon="more_vert" size={24} />}
            hideLabel
            menuItems={menuItems}
          />
        </Layout.Group>
      </div>
    );
  };

  /**
   * Renders the title cell as a link to the event details page.
   */
  const renderCellTitle = ({ cell, row }: { cell?: { value: any }; row: { original: EventEntity } }): ReactNode => {
    return <Link href={`/notifications/events/${row.original.eventId}`}>{cell?.value as string}</Link>;
  };

  /**
   * Renders the date range for the event.
   */
  const renderCellDates = ({ row }: { row: { original: EventEntity } }): ReactNode => {
    return (
      <React.Fragment>
        {dayjs(row.original.startDate).format('MM/DD/YYYY')} - {dayjs(row.original.endDate).format('MM/DD/YYYY')}
      </React.Fragment>
    );
  };

  /**
   * Renders the impacted systems cell, showing a tooltip if there are multiple systems.
   */
  const renderCellImpactedSystems = ({
    cell,
    row,
  }: {
    cell?: { value: any };
    row: { original: EventEntity };
  }): ReactNode => {
    if (row.original.impactedSystems?.length && row.original.impactedSystems.length > 1) {
      const sources: string[] = [];

      row.original.impactedSystems.forEach((impactedSystem: string) => {
        const system = assets?.ListImpactedSystems?.data?.find((asset: CodeAsset) => {
          return asset.codeId === impactedSystem;
        });

        if (!isUndefined(system)) {
          sources.push(system.codeDesc);
        }
      });

      return (
        <Tooltip content={<SourcesTooltip sources={sources} />} placement="auto">
          <span className="truncate">{sources.join(', ')}</span>
        </Tooltip>
      );
    }

    return cell?.value as string;
  };

  /**
   * Renders a count value with locale formatting.
   */
  const renderCellCount = ({ cell }: { cell?: { value: any } }): string => {
    return Number(cell?.value).toLocaleString('en-US');
  };

  /**
   * Renders the event type description based on the code.
   */
  const renderCellEventType = ({ cell }: { cell?: { value: any } }): string => {
    const theEventType = assets?.ListEventTypes?.data?.find((eventType: CodeAsset) => {
      return eventType.codeId === cell?.value;
    });

    return theEventType?.codeDesc ?? '';
  };

  /**
   * Columns configuration for the table.
   * Custom cell renderers are assigned for specific columns; others display their values by default as-is.
   */
  const columns: TableColumn[] = useMemo(() => {
    return orderBy(tableConfiguration?.initialColumns, ['order'], ['asc']).map((column: TableColumn) => {
      const theColumn = { ...column };

      if (theColumn.Header === 'Date(s)') {
        theColumn.Cell = renderCellDates;
      }

      if (theColumn.Header === 'Impacted Systems') {
        theColumn.Cell = renderCellImpactedSystems;
      }

      if (theColumn.Header === 'Title') {
        theColumn.Cell = renderCellTitle;
      }

      if (theColumn.Header === 'Event Type') {
        theColumn.Cell = renderCellEventType;
      }

      if (theColumn.Header.includes('Count')) {
        theColumn.Cell = renderCellCount;
      }

      if (theColumn.accessor === 'actions') {
        theColumn.Cell = renderCellActions;
      }

      return theColumn;
    });
  }, [assets]);

  return (
    <ErrorHandler location="src/entities/Event/ui/components/Table/Table.tsx">
      <Styles>
        <TableComponent
          columns={columns}
          configuration={tableConfiguration}
          error={error}
          headerLeft={<Filters requestArgs={requestArgs} setRequestArgs={setRequestArgs} />}
          isLoading={isLoading}
          noDataMessage={`No ${configuration.pluralName} Found.`}
          requestArgs={requestArgs}
          requestFunction={requestFunction}
          requestKey={requestKey}
          rows={rows}
          totalPages={totalPages}
          totalRecords={totalRecords}
        />
      </Styles>
    </ErrorHandler>
  );
}
